import React, { useState, useEffect } from "react";
import { Check, ChevronDown, AlertTriangle } from "lucide-react";
import { Autocomplete, TextField } from '@material-ui/core';
import crmProfessions from "../../components/dashboard/customer/professions";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";

interface ColumnMapperProps {
  csvHeaders: string[];
  csvData: string[][];
  onMappingComplete: (mapping: Record<string, string>) => void;
}

// Champs disponibles dans le CRM
const crmFields = [
  { id: "civility", label: "Civilité" },
  { id: "firstname", label: "Prénom" },
  { id: "lastname", label: "Nom" },
  { id: "phone", label: "Téléphone" },
  { id: "phone_fix", label: "Téléphone fixe" },
  { id: "email", label: "Email" },
  { id: "adress", label: "Adresse" },
  { id: "zip", label: "Code postal" },
  { id: "city", label: "Ville" },
  { id: "department", label: "Département" },
  { id: "region", label: "Région" },
  { id: "profession", label: "Profession" },
  { id: "adeli_rpps", label: "ADELI/RPPS" },
  { id: "antecedent", label: "Commentaire" },
  { id: "none", label: "Ne pas importer" },
];

// Professions disponibles dans le CRM
export const supportedProfessions = crmProfessions;

const ColumnMapper: React.FC<ColumnMapperProps> = ({ csvHeaders, csvData, onMappingComplete }) => {
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [professionMapping, setProfessionMapping] = useState<Record<string, string>>({});
  // const [previewData, setPreviewData] = useState<string[][]>([]);
  const [uniqueProfessions, setUniqueProfessions] = useState<string[]>([]);
  const [defaultProfession, setDefaultProfession] = useState<string>("");
  const [defaultProfessionError, setDefaultProfessionError] = useState<boolean>(false);

  // Détecter les professions uniques dans les données CSV
  useEffect(() => {
    if (csvData.length > 0) {
      const professionColumnIndex = csvHeaders.findIndex(header => 
        mapping[header] === "profession"
      );

      if (professionColumnIndex !== -1) {
        const professions = new Set<string>();
        csvData.slice(1).forEach(row => {
          if (row[professionColumnIndex]) {
            professions.add(row[professionColumnIndex].trim());
          }
        });
        setUniqueProfessions(Array.from(professions).sort());
      }
    }
  }, [csvData, csvHeaders, mapping]);

  // Essaie de faire une correspondance automatique basée sur les noms similaires
  useEffect(() => {
    const initialMapping: Record<string, string> = {};
    
    csvHeaders.forEach(header => {
      const normalizedHeader = header.toLowerCase().trim();
      
      // Cherche une correspondance dans les champs CRM
      const match = crmFields.find(field => 
        field.label.toLowerCase() === normalizedHeader ||
        field.id.toLowerCase() === normalizedHeader
      );
      
      if (match) {
        initialMapping[header] = match.id;
      } else {
        initialMapping[header] = "none";
      }
    });
    
    setMapping(initialMapping);
  }, [csvHeaders]);

  const handleMappingChange = (csvHeader: string, crmField: string) => {
    setMapping(prev => ({
      ...prev,
      [csvHeader]: crmField
    }));
  };

  const handleProfessionMapping = (csvValue: string, crmValue: string) => {
    setProfessionMapping(prev => ({
      ...prev,
      [csvValue]: crmValue
    }));
  };

  const handleSubmit = () => {
    // Fix typescript error by creating combined mapping
    const combinedMapping: Record<string, string> = { ...mapping };
    
    // Add profession mapping with prefix to avoid collision
    Object.entries(professionMapping).forEach(([key, value]) => {
      combinedMapping[`_profession_${key}`] = value;
    });
    // Add default profession to mapping so backend/front can fallback
    if (defaultProfession) {
      combinedMapping[`_profession_default`] = defaultProfession;
    }

    // Règle de validation:
    // - Si aucune colonne profession n'est mappée ET qu'aucune profession par défaut n'est choisie → bloquer
    // - Si colonne profession mappée mais des valeurs non mappées ET pas de défaut → bloquer
    const hasProfessionColumn = isProfessionMapped();
    const hasDefault = !!defaultProfession;
    const hasUnmappedValues = uniqueProfessions.length > 0 && uniqueProfessions.some((p) => !professionMapping[p]);

    if ((!hasProfessionColumn && !hasDefault) || (hasProfessionColumn && hasUnmappedValues && !hasDefault)) {
      setDefaultProfessionError(true);
      return;
    }
    
    onMappingComplete(combinedMapping);
  };

  const isProfessionMapped = () => {
    return Object.values(mapping).includes("profession");
  };

  const unmappedCount = uniqueProfessions.filter((p) => !professionMapping[p]).length;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
        <h3 className="text-lg font-medium mb-4">Associez les colonnes</h3>
        <p className="text-gray-600 mb-6">
          Pour chaque colonne de votre fichier CSV, sélectionnez le champ correspondant dans votre CRM.
        </p>
        
        <div className="space-y-3">
          {csvHeaders.map((header) => (
            <div key={header} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Badge variant="outline" className="mr-2">{header}</Badge>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex justify-between w-64 border-primary text-primary">
                    {crmFields.find(f => f.id === mapping[header])?.label || "Sélectionner"}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {crmFields.map((field) => (
                    <DropdownMenuItem 
                      key={field.id}
                      onClick={() => handleMappingChange(header, field.id)}
                      className="flex items-center justify-between"
                    >
                      {field.label}
                      {mapping[header] === field.id && <Check className="h-4 w-4" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      </div>

      
        <Card className="border-2 border-primary bg-background shadow-md">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold text-primary">Correspondance des professions</h3>
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary text-white">Important</span>
            </div>
            <p className="text-gray-700 mb-4">
              Cette étape impacte directement la qualité des filtres, statistiques et affectations. Vérifiez soigneusement chaque correspondance.
            </p>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm font-medium text-gray-800">Profession par défaut (pour valeurs non mappées)</span>
              <div style={{ minWidth: 280 }}>
                <Autocomplete
                  options={supportedProfessions.map((p: any) => p.profession)}
                  value={defaultProfession || null}
                  onChange={(event, newValue) => {
                    setDefaultProfession(newValue || "");
                    setDefaultProfessionError(false);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Profession par défaut" variant="outlined" />
                  )}
                />
              </div>
            </div>
            {defaultProfessionError && (
              <div className="mb-3 text-sm text-red-600">Veuillez sélectionner une profession par défaut pour continuer.</div>
            )}
            {unmappedCount > 0 && (
              <div className="mb-4 text-sm text-red-600">
                {unmappedCount} valeur(s) de profession non encore associée(s).
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {uniqueProfessions.map((csvProfession) => (
                <div key={csvProfession} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium">{csvProfession}</span>
                  </div>
                  <div style={{ minWidth: 220 }}>
                    <Autocomplete
                      options={supportedProfessions.map((p: any) => p.profession)}
                      value={professionMapping[csvProfession] || null}
                      onChange={(event, newValue) => {
                        handleProfessionMapping(csvProfession, newValue || "");
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Sélectionner" variant="outlined" />
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      <div className="flex justify-end">
        <Button onClick={handleSubmit} className="bg-primary hover:bg-opacity-90">
          Continuer
        </Button>
      </div>
    </div>
  );
};

export default ColumnMapper;
